import { Brain, ShieldCheck, Zap } from 'lucide-react'
import { type ReactNode, useRef } from 'react'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'
import { Button } from '@/components/ui/primitives.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * The first-use notice (AGM-009/010/124).
 *
 * A dialog rather than a banner because it is a decision point: the person is about to talk
 * to something that can act outside the chat or keep what they say, and the platform wants
 * a recorded moment where they were told. Acknowledgement is therefore an explicit button —
 * not a dismissal, not a scroll-past, not a pre-checked box.
 *
 * Escape maps to "Not now", not to acknowledgement. The requirement is that the notice is
 * only *cleared* by an explicit act; leaving is always allowed, and a dialog a keyboard user
 * cannot back out of would fail the same accessibility bar the trap exists to satisfy. The
 * chat stays blocked either way, so nothing is granted by walking away.
 *
 * The backdrop stays inert — no click handler, unlike the notifications drawer. A stray click
 * beside a dialog is not a decision, and this is the one dialog where dismissing by accident
 * would be indistinguishable from having read it.
 */
export const FirstUseNoticeDialog = ({
  agentName,
  capabilities,
  busy,
  failed,
  onAcknowledge,
  onDismiss
}: {
  agentName: string
  capabilities: Array<'actions' | 'memory'>
  busy: boolean
  failed: boolean
  onAcknowledge: () => void
  onDismiss: () => void
}) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  // Read here rather than at import: this notice is the one dialog whose wording is a record
  // of what somebody was told, and it must be in the language they are reading (PRD-27 F-3).
  const t = useT().marketplace.gallery.notice
  useFocusTrap(dialogRef, { onEscape: busy ? undefined : onDismiss })

  return (
    <Overlay>
      <div className="fade-in fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)]/45 p-4 backdrop-blur-[2px]">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="first-use-title"
          tabIndex={-1}
          // `shadow-frame` is a utility and therefore beats the base layer's focus ring, which
          // is what keeps the trap's programmatic focus from drawing a ring round the whole
          // dialog the moment it opens.
          className="w-full max-w-md overflow-clip rounded-shell border border-rule bg-card-solid shadow-frame focus:outline-none"
        >
          <div className="flex items-start gap-3 px-5 pt-5">
            <span
              aria-hidden
              className="mt-0.5 flex h-[30px] w-[30px] flex-none items-center justify-center rounded-tile border border-rule bg-grounded-soft text-grounded"
            >
              <ShieldCheck size={15} strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <h2 id="first-use-title" className="text-[18px]">
                {t.title(agentName)}
              </h2>
              <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">{t.intro}</p>
            </div>
          </div>

          <ul className="flex flex-col gap-2 px-5 py-4">
            {capabilities.includes('actions') && (
              <NoticeItem
                icon={<Zap size={13} strokeWidth={2.1} className="text-primary" />}
                title={t.actionsTitle}
                body={t.actionsBody}
              />
            )}
            {capabilities.includes('memory') && (
              <NoticeItem
                icon={<Brain size={13} strokeWidth={2.1} className="text-primary" />}
                title={t.memoryTitle}
                body={t.memoryBody}
              />
            )}
          </ul>

          {failed && (
            <p role="alert" className="px-5 pb-2 text-[13px] text-alert">
              {t.failed}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-rule px-5 py-3.5">
            <Button variant="ghost" onClick={onDismiss} disabled={busy}>
              {t.dismiss}
            </Button>
            <Button variant="primary" onClick={onAcknowledge} disabled={busy}>
              {busy ? t.acknowledging : t.acknowledge}
            </Button>
          </div>
        </div>
      </div>
    </Overlay>
  )
}

const NoticeItem = ({ icon, title, body }: { icon: ReactNode; title: string; body: string }) => (
  <li className="card flex items-start gap-2.5 rounded-card p-3">
    <span
      aria-hidden
      className="mt-px flex h-[26px] w-[26px] flex-none items-center justify-center rounded-tile border border-rule bg-card-solid"
    >
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block text-[13px] font-semibold tracking-[-0.012em]">{title}</span>
      <span className="mt-0.5 block text-pretty text-[12.5px] leading-relaxed text-fg-muted">{body}</span>
    </span>
  </li>
)
